import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import DateInputBR from "@/components/DateInputBR";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  FileText, Briefcase, UserMinus, PlusCircle, Send, Check, X, MessageCircleQuestion,
  Link2, Copy, Mail, MessageCircle, QrCode, Share2, Calendar, Clock,
  Building2, AlertCircle, CheckCircle2, ChevronRight, Trash2, ShieldCheck,
  FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { PageHero, KpiCard, Section } from "@/components/AnalyticsCommon";

const STATUS_LABEL = {
  pendente: { label: "Pendente", color: "bg-amber-100 text-amber-800 border-amber-300" },
  em_analise: { label: "Em análise", color: "bg-sky-100 text-sky-800 border-sky-300" },
  aprovado: { label: "Aprovado", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  reprovado: { label: "Reprovado", color: "bg-rose-100 text-rose-800 border-rose-300" },
  ajustes_solicitados: { label: "Ajustes solicitados", color: "bg-violet-100 text-violet-800 border-violet-300" },
};

const URG_LABEL = {
  baixa: { l: "Baixa", c: "bg-slate-100 text-slate-700" },
  normal: { l: "Normal", c: "bg-sky-100 text-sky-700" },
  alta: { l: "Alta", c: "bg-amber-100 text-amber-800" },
  critica: { l: "Crítica", c: "bg-rose-100 text-rose-800" },
};

const MOTIVO_DESLIG = [
  { v: "pedido_demissao", l: "Pedido de demissão" },
  { v: "dispensa_sem_justa_causa", l: "Dispensa sem justa causa" },
  { v: "dispensa_com_justa_causa", l: "Dispensa com justa causa" },
  { v: "rescisao_acordada", l: "Rescisão acordada" },
  { v: "termino_contrato_experiencia", l: "Término contrato experiência" },
  { v: "termino_contrato_determinado", l: "Término contrato determinado" },
  { v: "aposentadoria", l: "Aposentadoria" },
];

export default function SmartFormsPage() {
  const { companies } = useCompany();
  const [tab, setTab] = useState("dashboard");
  const [filters, setFilters] = useState({ type: null, status: null, company_id: null });
  const [dashboard, setDashboard] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [links, setLinks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newVacancyOpen, setNewVacancyOpen] = useState(false);
  const [newTerminationOpen, setNewTerminationOpen] = useState(false);
  const [newLinkOpen, setNewLinkOpen] = useState(false);

  const loadDashboard = async () => {
    try {
      const params = {};
      if (filters.company_id) params.company_id = filters.company_id;
      const { data } = await api.get("/forms/dashboard", { params });
      setDashboard(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      if (filters.company_id) params.company_id = filters.company_id;
      const { data } = await api.get("/forms/submissions", { params });
      setSubmissions(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const loadLinks = async () => {
    try {
      const { data } = await api.get("/forms/links");
      setLinks(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === "dashboard") loadDashboard();
    if (tab === "submissoes") loadSubmissions();
    if (tab === "links") loadLinks();
  }, [tab, JSON.stringify(filters)]);

  const approve = async (sid, comentario) => {
    try {
      await api.post(`/forms/submissions/${sid}/approve`, { comentario: comentario || "" });
      toast.success("Aprovado e ações automáticas executadas");
      setSelected(null);
      loadSubmissions();
      loadDashboard();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reject = async (sid, comentario) => {
    if (!comentario?.trim()) { toast.error("Comentário obrigatório"); return; }
    try {
      await api.post(`/forms/submissions/${sid}/reject`, { comentario });
      toast.success("Rejeitado");
      setSelected(null);
      loadSubmissions();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const requestChanges = async (sid, comentario) => {
    if (!comentario?.trim()) { toast.error("Comentário obrigatório"); return; }
    try {
      await api.post(`/forms/submissions/${sid}/request-changes`, { comentario });
      toast.success("Ajustes solicitados");
      setSelected(null);
      loadSubmissions();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4 pb-10" data-testid="smart-forms-page">
      <PageHero
        kicker={<><FileText className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Workflow Inteligente</>}
        title="Formulários Inteligentes"
        description="Solicitações de vaga e desligamento com workflow de aprovação RH, links compartilháveis e auditoria completa."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setNewVacancyOpen(true)}
                    className="h-10 bg-white/15 hover:bg-white/25 text-white" data-testid="btn-nova-vaga">
              <Briefcase className="h-3.5 w-3.5 mr-1.5" /> Nova Solicitação de Vaga
            </Button>
            <Button size="sm" onClick={() => setNewTerminationOpen(true)}
                    className="h-10 bg-white/15 hover:bg-white/25 text-white" data-testid="btn-novo-desligamento">
              <UserMinus className="h-3.5 w-3.5 mr-1.5" /> Solicitação de Desligamento
            </Button>
            <Button size="sm" onClick={() => setNewLinkOpen(true)}
                    className="h-10 bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]" data-testid="btn-novo-link">
              <Link2 className="h-3.5 w-3.5 mr-1.5" /> Gerar Link Público
            </Button>
          </div>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="submissoes" data-testid="tab-submissoes">Submissões</TabsTrigger>
          <TabsTrigger value="links" data-testid="tab-links">Links Públicos</TabsTrigger>
        </TabsList>

        {/* Dashboard */}
        <TabsContent value="dashboard" className="space-y-4 mt-4">
          {dashboard && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <KpiCard label="Total" value={dashboard.kpis.total} icon={FileText} accent="slate" />
                <KpiCard label="Pendentes" value={dashboard.kpis.pendentes} icon={Clock} accent="ochre" />
                <KpiCard label="Aprovados" value={dashboard.kpis.aprovados} icon={CheckCircle2} accent="emerald" />
                <KpiCard label="Reprovados" value={dashboard.kpis.reprovados} icon={X} accent="rose" />
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <KpiCard label="Vagas" value={dashboard.kpis.vagas} icon={Briefcase} accent="sky" />
                <KpiCard label="Desligamentos" value={dashboard.kpis.desligamentos} icon={UserMinus} accent="rose" />
                <KpiCard label="Ajustes solicitados" value={dashboard.kpis.ajustes_solicitados} icon={MessageCircleQuestion} accent="violet" />
                <KpiCard label="Tempo médio aprovação"
                        value={dashboard.kpis.tempo_medio_aprovacao_h !== null ? `${dashboard.kpis.tempo_medio_aprovacao_h}h` : "—"}
                        icon={Clock} accent="slate" />
              </div>

              <Section kicker="Últimas submissões" title="Atividade recente">
                <div className="space-y-2">
                  {dashboard.recent.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground italic">Nenhuma submissão ainda.</div>
                  ) : dashboard.recent.map((r) => {
                    const s = STATUS_LABEL[r.status] || STATUS_LABEL.pendente;
                    return (
                      <div key={r.id} className="flex items-center justify-between border border-border rounded-md px-3 py-2 hover:bg-slate-50">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`p-2 rounded ${r.type === "vacancy_request" ? "bg-sky-100" : "bg-rose-100"}`}>
                            {r.type === "vacancy_request" ? <Briefcase className="h-4 w-4 text-sky-700" /> : <UserMinus className="h-4 w-4 text-rose-700" />}
                          </div>
                          <div className="min-w-0">
                            <div className="text-[12.5px] font-medium truncate">{r.cargo_or_employee || "—"}</div>
                            <div className="text-[11px] text-muted-foreground truncate">{r.protocolo} · {r.supervisor || "—"}</div>
                          </div>
                        </div>
                        <Badge className={`${s.color} text-[10.5px] uppercase`}>{s.label}</Badge>
                      </div>
                    );
                  })}
                </div>
              </Section>
            </>
          )}
        </TabsContent>

        {/* Submissões */}
        <TabsContent value="submissoes" className="space-y-3 mt-4">
          <div className="flex flex-wrap gap-2">
            <Select value={filters.type || "all"}
                    onValueChange={(v) => setFilters({ ...filters, type: v === "all" ? null : v })}>
              <SelectTrigger className="w-[200px] h-9" data-testid="filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="vacancy_request">Solicitação de Vaga</SelectItem>
                <SelectItem value="termination_request">Solicitação de Desligamento</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.status || "all"}
                    onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? null : v })}>
              <SelectTrigger className="w-[200px] h-9" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="pendente">Pendente</SelectItem>
                <SelectItem value="aprovado">Aprovado</SelectItem>
                <SelectItem value="reprovado">Reprovado</SelectItem>
                <SelectItem value="ajustes_solicitados">Ajustes solicitados</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? <div className="text-center py-10 text-muted-foreground">Carregando...</div> :
            submissions.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground italic">Nenhuma submissão encontrada com esses filtros.</div>
            ) : (
              <div className="space-y-2">
                {submissions.map((s) => {
                  const st = STATUS_LABEL[s.status] || STATUS_LABEL.pendente;
                  const d = s.data || {};
                  return (
                    <div key={s.id}
                         onClick={() => setSelected(s)}
                         className="border border-border rounded-lg px-4 py-3 hover:bg-slate-50 cursor-pointer flex items-start gap-3"
                         data-testid={`submission-${s.id}`}>
                      <div className={`p-2.5 rounded ${s.type === "vacancy_request" ? "bg-sky-100" : "bg-rose-100"}`}>
                        {s.type === "vacancy_request" ? <Briefcase className="h-4 w-4 text-sky-700" /> : <UserMinus className="h-4 w-4 text-rose-700" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[11.5px] text-muted-foreground">{s.protocolo}</span>
                          <Badge className={`${st.color} text-[10.5px] uppercase`}>{st.label}</Badge>
                          {s.type === "vacancy_request" && d.grau_urgencia && (
                            <Badge variant="outline" className={URG_LABEL[d.grau_urgencia]?.c}>
                              {URG_LABEL[d.grau_urgencia]?.l}
                            </Badge>
                          )}
                          {s.created_via === "public_link" && (
                            <Badge variant="outline" className="text-[10px] text-violet-700 border-violet-300">via link público</Badge>
                          )}
                        </div>
                        <div className="text-[13px] font-semibold mt-1">{d.cargo || d.employee_nome || "—"}</div>
                        <div className="text-[11.5px] text-muted-foreground">
                          {d.supervisor_nome} · {d.sector_nome || "—"} · {new Date(s.created_at).toLocaleString("pt-BR")}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground self-center" />
                    </div>
                  );
                })}
              </div>
            )}
        </TabsContent>

        {/* Links */}
        <TabsContent value="links" className="space-y-3 mt-4">
          {links.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground italic">Nenhum link criado. Clique em "Gerar Link Público" no topo.</div>
          ) : (
            <div className="space-y-2">
              {links.map((l) => (
                <LinkCard key={l.id} link={l} onDeactivate={async () => {
                  await api.delete(`/forms/links/${l.id}`);
                  toast.success("Link desativado");
                  loadLinks();
                }} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <NewVacancyDialog open={newVacancyOpen} onOpenChange={setNewVacancyOpen}
                        companies={companies} onCreated={() => { loadSubmissions(); loadDashboard(); }} />
      <NewTerminationDialog open={newTerminationOpen} onOpenChange={setNewTerminationOpen}
                            companies={companies} onCreated={() => { loadSubmissions(); loadDashboard(); }} />
      <NewLinkDialog open={newLinkOpen} onOpenChange={setNewLinkOpen}
                     companies={companies} onCreated={() => loadLinks()} />
      <SubmissionDetailDialog
        submission={selected} onClose={() => setSelected(null)}
        onApprove={approve} onReject={reject} onRequestChanges={requestChanges}
      />
    </div>
  );
}

// =================================================================
//  Link Card
// =================================================================
function LinkCard({ link, onDeactivate }) {
  const publicUrl = `${window.location.origin}/f/${link.token}`;
  const copyLink = () => { navigator.clipboard.writeText(publicUrl); toast.success("Link copiado!"); };
  const shareWhats = () => {
    const msg = encodeURIComponent(`Olá! Preencha a solicitação aqui: ${publicUrl}`);
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };
  const shareEmail = () => {
    const subj = encodeURIComponent(link.titulo || "Solicitação RH");
    const body = encodeURIComponent(`Olá,\n\nAcesse o formulário: ${publicUrl}\n\nObrigado.`);
    window.location.href = `mailto:?subject=${subj}&body=${body}`;
  };
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(publicUrl)}`;
  const expired = link.expires_at && link.expires_at < new Date().toISOString();
  return (
    <div className="border border-border rounded-lg p-4 bg-white" data-testid={`link-${link.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className={link.type === "vacancy_request" ? "bg-sky-50 text-sky-800 border-sky-300" : "bg-rose-50 text-rose-800 border-rose-300"}>
              {link.type === "vacancy_request" ? "Vaga" : "Desligamento"}
            </Badge>
            {!link.active && <Badge variant="outline" className="bg-slate-100 text-slate-700">Desativado</Badge>}
            {expired && <Badge variant="outline" className="bg-amber-100 text-amber-800">Expirado</Badge>}
          </div>
          <div className="font-semibold text-[13px]">{link.titulo}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {link.uses} envio(s){link.max_uses ? ` / ${link.max_uses}` : ""} · Expira em {link.expires_at ? new Date(link.expires_at).toLocaleDateString("pt-BR") : "—"}
          </div>
        </div>
        {link.active && (
          <Button size="sm" variant="ghost" onClick={onDeactivate}
                  className="text-rose-600 h-8" data-testid={`deactivate-${link.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <input readOnly value={publicUrl}
               className="flex-1 min-w-[200px] text-[11.5px] font-mono px-2 py-1.5 bg-slate-50 border border-border rounded" />
        <Button size="sm" variant="outline" onClick={copyLink} className="h-8" data-testid={`copy-${link.id}`}>
          <Copy className="h-3.5 w-3.5 mr-1" /> Copiar
        </Button>
        <Button size="sm" variant="outline" onClick={shareWhats} className="h-8 text-emerald-700">
          <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
        </Button>
        <Button size="sm" variant="outline" onClick={shareEmail} className="h-8 text-sky-700">
          <Mail className="h-3.5 w-3.5 mr-1" /> E-mail
        </Button>
        <Dialog>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" className="h-8">
              <QrCode className="h-3.5 w-3.5 mr-1" /> QR Code
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>QR Code · {link.titulo}</DialogTitle></DialogHeader>
            <div className="flex flex-col items-center gap-3 py-4">
              <img src={qrUrl} alt="QR" className="border rounded" />
              <div className="text-[11px] text-muted-foreground break-all text-center font-mono">{publicUrl}</div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// =================================================================
//  Dialogs
// =================================================================
function SubmissionDetailDialog({ submission, onClose, onApprove, onReject, onRequestChanges }) {
  const [comment, setComment] = useState("");
  if (!submission) return null;
  const d = submission.data || {};
  const isVacancy = submission.type === "vacancy_request";
  const isPending = submission.status === "pendente" || submission.status === "ajustes_solicitados";
  return (
    <Dialog open={!!submission} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="submission-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isVacancy ? <Briefcase className="h-4 w-4" /> : <UserMinus className="h-4 w-4" />}
            {isVacancy ? "Solicitação de Vaga" : "Solicitação de Desligamento"}
            <span className="font-mono text-[12px] text-muted-foreground ml-2">{submission.protocolo}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-[12.5px]">
          <Section kicker="Dados" title="">
            {isVacancy ? (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                <KV k="Cargo" v={d.cargo} />
                <KV k="Quantidade" v={d.quantidade} />
                <KV k="Tipo" v={d.tipo_contratacao} />
                <KV k="Urgência" v={d.grau_urgencia} />
                <KV k="Salário" v={d.salario_proposto ? `R$ ${d.salario_proposto}` : "—"} />
                <KV k="Horário" v={d.horario} />
                <KV k="Escala" v={d.escala} />
                <KV k="Data prevista" v={d.data_prevista} />
                <KV k="Motivo" v={d.motivo_contratacao} />
                <KV k="Setor" v={d.sector_nome} />
                <KV k="Unidade" v={d.unidade} />
                <KV k="Supervisor" v={d.supervisor_nome} />
                <KV k="Benefícios" v={d.beneficios} full />
                <KV k="Atividades" v={d.descricao_atividades} full />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                <KV k="Colaborador" v={d.employee_nome} />
                <KV k="Matrícula" v={d.employee_matricula} />
                <KV k="Setor" v={d.sector_nome} />
                <KV k="Supervisor" v={d.supervisor_nome} />
                <KV k="Motivo" v={d.motivo_desligamento} />
                <KV k="Data sugerida" v={d.data_sugerida_desligamento} />
                <KV k="Repor?" v={d.deseja_reposicao ? "Sim" : "Não"} />
                <KV k="Justificativa" v={d.justificativa} full />
                <KV k="Advertências" v={d.advertencias} full />
                <KV k="Suspensões" v={d.suspensoes} full />
                <KV k="Ocorrências" v={d.ocorrencias} full />
              </div>
            )}
          </Section>

          {isVacancy && d.tem_candidato_definido && d.candidato && (
            <Section kicker="Candidato indicado" title="">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                <KV k="Nome" v={d.candidato.nome} />
                <KV k="CPF" v={d.candidato.cpf} />
                <KV k="RG" v={d.candidato.rg} />
                <KV k="CNH" v={d.candidato.cnh_categoria} />
                <KV k="Escolaridade" v={d.candidato.escolaridade} />
                <KV k="Telefone" v={d.candidato.telefone} />
                <KV k="E-mail" v={d.candidato.email} />
                <KV k="Endereço" v={d.candidato.endereco} full />
                <KV k="Observações" v={d.candidato.observacoes} full />
              </div>
              <div className="mt-2 text-[10.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-start gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 mt-0.5" />
                Dados pessoais isolados (LGPD) — visíveis apenas ao RH para fins de contratação.
              </div>
            </Section>
          )}

          <Section kicker="Auditoria" title="">
            <ul className="text-[11.5px] space-y-1.5">
              {(submission.workflow_history || []).map((h, i) => (
                <li key={`${h.ts}-${i}`} className="flex items-start gap-2">
                  <span className="font-mono text-[10.5px] text-muted-foreground">{new Date(h.ts).toLocaleString("pt-BR")}</span>
                  <span><b>{h.action}</b> · {h.user_name || h.supervisor || "—"} {h.ip && <span className="text-muted-foreground">({h.ip})</span>}</span>
                </li>
              ))}
            </ul>
          </Section>

          {submission.generated_entities?.length > 0 && (
            <div className="rounded bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11.5px] text-emerald-800">
              <b>Entidades geradas após aprovação:</b>
              <ul className="mt-1 ml-3 list-disc">
                {submission.generated_entities.map((e) => (
                  <li key={e.id}>{e.type}: <span className="font-mono text-[10.5px]">{e.id}</span></li>
                ))}
              </ul>
            </div>
          )}

          {isPending && (
            <div className="border-t pt-3">
              <Label className="text-[11px] uppercase tracking-wider">Comentário (obrigatório para rejeitar/ajustar)</Label>
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
                        data-testid="workflow-comment" />
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={async () => {
            try {
              const res = await api.get(`/forms/submissions/${submission.id}/pdf`, { responseType: "blob" });
              const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = `${submission.protocolo || submission.id}.pdf`;
              a.click();
              URL.revokeObjectURL(url);
              toast.success("PDF baixado!");
            } catch (e) { toast.error("Erro ao gerar PDF"); }
          }} className="border-[#C5A059] text-[#8A6B2B]" data-testid="btn-download-pdf">
            <FileDown className="h-3.5 w-3.5 mr-1" /> PDF Executivo
          </Button>
          {isPending ? (
            <>
              <Button variant="outline" onClick={() => onRequestChanges(submission.id, comment)}
                      className="border-violet-300 text-violet-700"
                      data-testid="btn-request-changes">
                <MessageCircleQuestion className="h-3.5 w-3.5 mr-1" /> Solicitar ajustes
              </Button>
              <Button variant="outline" onClick={() => onReject(submission.id, comment)}
                      className="border-rose-300 text-rose-700"
                      data-testid="btn-reject">
                <X className="h-3.5 w-3.5 mr-1" /> Rejeitar
              </Button>
              <Button onClick={() => onApprove(submission.id, comment)}
                      className="bg-emerald-700 hover:bg-emerald-800"
                      data-testid="btn-approve">
                <Check className="h-3.5 w-3.5 mr-1" /> Aprovar e processar
              </Button>
            </>
          ) : <Button variant="outline" onClick={onClose}>Fechar</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KV({ k, v, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
      <div className="font-medium">{v || "—"}</div>
    </div>
  );
}

function NewVacancyDialog({ open, onOpenChange, companies, onCreated }) {
  const [sectors, setSectors] = useState([]);
  const [form, setForm] = useState({
    company_id: null, sector_id: null, unidade: "",
    supervisor_nome: "", supervisor_email: "",
    cargo: "", quantidade: 1, motivo_contratacao: "aumento_quadro",
    tipo_contratacao: "CLT", horario: "", escala: "",
    salario_proposto: "", beneficios: "", data_prevista: "",
    grau_urgencia: "normal", descricao_atividades: "",
    tem_candidato_definido: false,
    candidato: { nome: "", cpf: "", rg: "", cnh_categoria: "", escolaridade: "",
                 endereco: "", telefone: "", email: "", observacoes: "" },
  });

  useEffect(() => {
    if (!form.company_id) { setSectors([]); return; }
    api.get(`/sectors?company_id=${form.company_id}`).then((r) => setSectors(r.data || []));
  }, [form.company_id]);

  const submit = async () => {
    if (!form.cargo || !form.supervisor_nome || !form.motivo_contratacao) {
      toast.error("Preencha cargo, supervisor e motivo");
      return;
    }
    try {
      const data = { ...form };
      if (data.company_id) data.company_nome = companies.find((c) => c.id === data.company_id)?.nome_fantasia;
      if (data.sector_id) data.sector_nome = sectors.find((s) => s.id === data.sector_id)?.name;
      if (data.salario_proposto) data.salario_proposto = parseFloat(data.salario_proposto);
      await api.post("/forms/submissions", { type: "vacancy_request", data });
      toast.success("Solicitação enviada · RH avaliará");
      onOpenChange(false);
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle><Briefcase className="inline h-4 w-4 mr-1.5" /> Nova Solicitação de Vaga</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Sel label="Empresa" value={form.company_id || ""} setValue={(v) => setForm({ ...form, company_id: v || null })}
                 options={companies.map((c) => ({ v: c.id, l: c.nome_fantasia || c.razao_social }))} />
            <Sel label="Setor" value={form.sector_id || ""} setValue={(v) => setForm({ ...form, sector_id: v || null })}
                 options={sectors.map((s) => ({ v: s.id, l: s.name }))} />
            <Inp label="Unidade" value={form.unidade} onChange={(v) => setForm({ ...form, unidade: v })} />
            <Inp label="Supervisor solicitante" value={form.supervisor_nome} onChange={(v) => setForm({ ...form, supervisor_nome: v })} testId="vac-supervisor" />
            <Inp label="E-mail supervisor" value={form.supervisor_email} onChange={(v) => setForm({ ...form, supervisor_email: v })} />
          </div>
          <div className="grid grid-cols-2 gap-3 border-t pt-3">
            <Inp label="Cargo" value={form.cargo} onChange={(v) => setForm({ ...form, cargo: v })} testId="vac-cargo" />
            <Inp label="Quantidade" type="number" value={form.quantidade} onChange={(v) => setForm({ ...form, quantidade: parseInt(v) || 1 })} />
            <Sel label="Motivo" value={form.motivo_contratacao} setValue={(v) => setForm({ ...form, motivo_contratacao: v })}
                 options={[
                   { v: "substituicao", l: "Substituição" },
                   { v: "aumento_quadro", l: "Aumento de quadro" },
                   { v: "projeto", l: "Projeto específico" },
                   { v: "outros", l: "Outros" },
                 ]} />
            <Sel label="Tipo contratação" value={form.tipo_contratacao} setValue={(v) => setForm({ ...form, tipo_contratacao: v })}
                 options={[
                   { v: "CLT", l: "CLT" }, { v: "PJ", l: "PJ" },
                   { v: "Temporario", l: "Temporário" }, { v: "Aprendiz", l: "Aprendiz" },
                   { v: "Estagio", l: "Estágio" }, { v: "Outros", l: "Outros" },
                 ]} />
            <Inp label="Horário" value={form.horario} onChange={(v) => setForm({ ...form, horario: v })} />
            <Inp label="Escala" value={form.escala} onChange={(v) => setForm({ ...form, escala: v })} />
            <Inp label="Salário proposto" type="number" value={form.salario_proposto} onChange={(v) => setForm({ ...form, salario_proposto: v })} />
            <Inp label="Data prevista" type="date" value={form.data_prevista} onChange={(v) => setForm({ ...form, data_prevista: v })} />
            <Sel label="Grau de urgência" value={form.grau_urgencia} setValue={(v) => setForm({ ...form, grau_urgencia: v })}
                 options={[{ v: "baixa", l: "Baixa" }, { v: "normal", l: "Normal" }, { v: "alta", l: "Alta" }, { v: "critica", l: "Crítica" }]} />
            <Inp label="Benefícios" value={form.beneficios} onChange={(v) => setForm({ ...form, beneficios: v })} />
            <Inp label="Atividades / Descrição" value={form.descricao_atividades} onChange={(v) => setForm({ ...form, descricao_atividades: v })} full />
          </div>
          <div className="flex items-center gap-2 border-t pt-3">
            <Switch checked={form.tem_candidato_definido}
                    onCheckedChange={(v) => setForm({ ...form, tem_candidato_definido: v })}
                    data-testid="vac-tem-candidato" />
            <Label className="text-[12px] font-medium">Já tenho candidato indicado</Label>
          </div>
          {form.tem_candidato_definido && (
            <div className="grid grid-cols-2 gap-3 rounded bg-amber-50/50 border border-amber-200 p-3">
              <div className="col-span-2 text-[10.5px] text-amber-700 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Dados pessoais isolados (LGPD) — visíveis apenas ao RH.
              </div>
              <Inp label="Nome completo" value={form.candidato.nome} onChange={(v) => setForm({ ...form, candidato: { ...form.candidato, nome: v } })} />
              <Inp label="CPF" value={form.candidato.cpf} onChange={(v) => setForm({ ...form, candidato: { ...form.candidato, cpf: v } })} />
              <Inp label="RG" value={form.candidato.rg} onChange={(v) => setForm({ ...form, candidato: { ...form.candidato, rg: v } })} />
              <Inp label="Categoria CNH" value={form.candidato.cnh_categoria} onChange={(v) => setForm({ ...form, candidato: { ...form.candidato, cnh_categoria: v } })} />
              <Inp label="Escolaridade" value={form.candidato.escolaridade} onChange={(v) => setForm({ ...form, candidato: { ...form.candidato, escolaridade: v } })} />
              <Inp label="Telefone" value={form.candidato.telefone} onChange={(v) => setForm({ ...form, candidato: { ...form.candidato, telefone: v } })} />
              <Inp label="E-mail" value={form.candidato.email} onChange={(v) => setForm({ ...form, candidato: { ...form.candidato, email: v } })} />
              <Inp label="Endereço" value={form.candidato.endereco} onChange={(v) => setForm({ ...form, candidato: { ...form.candidato, endereco: v } })} full />
              <Inp label="Observações" value={form.candidato.observacoes} onChange={(v) => setForm({ ...form, candidato: { ...form.candidato, observacoes: v } })} full />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="vac-submit">
            <Send className="h-3.5 w-3.5 mr-1" /> Enviar para RH
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewTerminationDialog({ open, onOpenChange, companies, onCreated }) {
  const [employees, setEmployees] = useState([]);
  const [empSearch, setEmpSearch] = useState("");
  const [form, setForm] = useState({
    employee_id: null, employee_matricula: "", employee_nome: "",
    company_id: null, sector_id: null,
    supervisor_nome: "", supervisor_email: "",
    motivo_desligamento: "pedido_demissao",
    justificativa: "", advertencias: "", suspensoes: "", ocorrencias: "",
    deseja_reposicao: false, data_sugerida_desligamento: "",
  });

  useEffect(() => {
    if (!empSearch || empSearch.length < 2) { setEmployees([]); return; }
    const t = setTimeout(() => {
      api.get(`/employees?search=${encodeURIComponent(empSearch)}&page_size=10`)
        .then((r) => setEmployees(r.data.items || []));
    }, 300);
    return () => clearTimeout(t);
  }, [empSearch]);

  const submit = async () => {
    if (!form.employee_nome || !form.supervisor_nome || !form.justificativa) {
      toast.error("Preencha colaborador, supervisor e justificativa");
      return;
    }
    try {
      await api.post("/forms/submissions", { type: "termination_request", data: form });
      toast.success("Solicitação enviada · RH avaliará");
      onOpenChange(false);
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle><UserMinus className="inline h-4 w-4 mr-1.5" /> Solicitação de Desligamento</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Buscar colaborador</Label>
            <Input placeholder="Digite o nome ou matrícula..." value={empSearch}
                   onChange={(e) => setEmpSearch(e.target.value)}
                   data-testid="term-emp-search" />
            {employees.length > 0 && (
              <div className="border border-border rounded mt-1 max-h-[150px] overflow-y-auto">
                {employees.map((e) => (
                  <div key={e.id}
                       onClick={() => {
                         setForm({
                           ...form, employee_id: e.id, employee_matricula: e.matricula,
                           employee_nome: e.nome, company_id: e.company_id, sector_id: e.sector_id,
                         });
                         setEmpSearch("");
                         setEmployees([]);
                       }}
                       className="px-3 py-1.5 hover:bg-slate-100 cursor-pointer text-[12px]"
                       data-testid={`term-emp-${e.id}`}>
                    <span className="font-mono text-[11px]">{e.matricula}</span> · {e.nome} · <span className="text-muted-foreground">{e.cargo}</span>
                  </div>
                ))}
              </div>
            )}
            {form.employee_nome && (
              <div className="mt-1 rounded bg-emerald-50 border border-emerald-200 px-2 py-1.5 text-[12px]">
                Selecionado: <b>{form.employee_nome}</b> ({form.employee_matricula})
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 border-t pt-3">
            <Inp label="Supervisor" value={form.supervisor_nome} onChange={(v) => setForm({ ...form, supervisor_nome: v })} testId="term-supervisor" />
            <Inp label="E-mail supervisor" value={form.supervisor_email} onChange={(v) => setForm({ ...form, supervisor_email: v })} />
            <Sel label="Motivo desligamento" value={form.motivo_desligamento}
                 setValue={(v) => setForm({ ...form, motivo_desligamento: v })}
                 options={MOTIVO_DESLIG.map((m) => ({ v: m.v, l: m.l }))} testId="term-motivo" />
            <Inp label="Data sugerida" type="date" value={form.data_sugerida_desligamento}
                 onChange={(v) => setForm({ ...form, data_sugerida_desligamento: v })} />
            <Inp label="Justificativa" value={form.justificativa} onChange={(v) => setForm({ ...form, justificativa: v })} full />
            <Inp label="Advertências" value={form.advertencias} onChange={(v) => setForm({ ...form, advertencias: v })} full />
            <Inp label="Suspensões" value={form.suspensoes} onChange={(v) => setForm({ ...form, suspensoes: v })} full />
            <Inp label="Ocorrências" value={form.ocorrencias} onChange={(v) => setForm({ ...form, ocorrencias: v })} full />
          </div>
          <div className="flex items-center gap-2 border-t pt-3">
            <Switch checked={form.deseja_reposicao}
                    onCheckedChange={(v) => setForm({ ...form, deseja_reposicao: v })}
                    data-testid="term-repor" />
            <Label className="text-[12px] font-medium">Deseja reposição da vaga?</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} className="bg-rose-700 hover:bg-rose-800" data-testid="term-submit">
            <Send className="h-3.5 w-3.5 mr-1" /> Enviar para RH
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewLinkDialog({ open, onOpenChange, companies, onCreated }) {
  const [form, setForm] = useState({
    type: "vacancy_request", titulo: "", company_id: null,
    expires_in_days: 30, max_uses: "",
  });
  const submit = async () => {
    try {
      const payload = { ...form };
      if (payload.max_uses) payload.max_uses = parseInt(payload.max_uses);
      else delete payload.max_uses;
      await api.post("/forms/links", payload);
      toast.success("Link público criado!");
      onOpenChange(false);
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle><Link2 className="inline h-4 w-4 mr-1.5" /> Gerar Link Público</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Sel label="Tipo de formulário" value={form.type} setValue={(v) => setForm({ ...form, type: v })}
               options={[
                 { v: "vacancy_request", l: "Solicitação de Vaga" },
                 { v: "termination_request", l: "Solicitação de Desligamento" },
               ]} testId="link-type" />
          <Inp label="Título" value={form.titulo} onChange={(v) => setForm({ ...form, titulo: v })}
               placeholder="Ex: Solicitação de Vagas · Q3 2026" testId="link-titulo" />
          <Sel label="Empresa (opcional)" value={form.company_id || ""}
               setValue={(v) => setForm({ ...form, company_id: v || null })}
               options={companies.map((c) => ({ v: c.id, l: c.nome_fantasia || c.razao_social }))} />
          <div className="grid grid-cols-2 gap-3">
            <Inp label="Expira em (dias)" type="number" value={form.expires_in_days}
                 onChange={(v) => setForm({ ...form, expires_in_days: parseInt(v) || 30 })} />
            <Inp label="Limite envios (opcional)" type="number" value={form.max_uses}
                 onChange={(v) => setForm({ ...form, max_uses: v })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} className="bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]" data-testid="link-create">
            <Share2 className="h-3.5 w-3.5 mr-1" /> Criar Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Inp({ label, value, onChange, type, placeholder, full, testId }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider">{label}</Label>
      {type === "date" ? (
        <DateInputBR value={value} onChange={onChange} testId={testId} />
      ) : (
        <Input type={type || "text"} value={value || ""}
               onChange={(e) => onChange(e.target.value)}
               placeholder={placeholder}
               data-testid={testId} />
      )}
    </div>
  );
}
function Sel({ label, value, setValue, options, testId }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider">{label}</Label>
      <Select value={value || "_"} onValueChange={(v) => setValue(v === "_" ? "" : v)}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder="— selecione —" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="_">— selecione —</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
